import React, { useState, useEffect } from 'react';
import { Upload, Input, Space, Typography, message, Table, Button } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadAdditionalDocument } from '@app/api/export.api';

const { Dragger } = Upload;
const { Text } = Typography;

interface AdditionalDocumentProps {
  export_id: string;
  editable?: boolean;
  status: string | null;
  additionalSubmittedDocs: {
    document_id: string;
    name: string;
    file: string;
    document_url: string;
  }[];
  onChange?: (file: UploadFile, documentName: string, documentId: string) => void;
  editableUser: any;
}

const generateDocumentId = (name: string): string => {
  return name.trim().toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
};

const AdditionalDocument: React.FC<AdditionalDocumentProps> = ({
  export_id,
  additionalSubmittedDocs,
  editable,
  editableUser,
  onChange,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [documents, setDocuments] = useState(additionalSubmittedDocs || []);

  useEffect(() => {
    setDocuments(additionalSubmittedDocs || []);
  }, [additionalSubmittedDocs]);

  const props: UploadProps = {
    multiple: false,
    beforeUpload: async (file) => {
      if (!documentName) {
        message.error('Please enter a document name first.');
        return Upload.LIST_IGNORE;
      }

      const documentId = editingDocumentId ?? generateDocumentId(documentName);
      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('name', documentName);
        formData.append('document_id', documentId);
        formData.append('file', file);

        const response = await uploadAdditionalDocument(Number(export_id), formData);

        const updatedDocuments =
          response?.data?.export_additional_documents?.map((doc) => ({
            document_id: doc.document_id,
            name: doc.name,
            file: doc.file,
            document_url: doc.document_url,
          })) ?? [];

        if (!updatedDocuments) {
          message.error('Invalid response from server');
          return Upload.LIST_IGNORE;
        }

        setDocuments(updatedDocuments);

        if (onChange) {
          onChange(file as UploadFile, documentName, documentId);
        }

        message.success(`${file.name} uploaded successfully`);

        setDocumentName('');
        setFileList([]);
        setEditingDocumentId(null);
      } catch (err) {
        message.error('Failed to upload file.');
      } finally {
        setUploading(false);
      }

      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
    showUploadList: true,
  };

  const handleView = (_documentId: string, fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const handleEdit = (record: { document_id: string; name: string }) => {
    setDocumentName(record.name);
    setEditingDocumentId(record.document_id);
    setFileList([]);
    message.info(`Editing "${record.name}"`);
  };

  const handleNameOnlyUpdate = async () => {
    if (!documentName || !editingDocumentId) {
      message.error('Missing document name or ID.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', documentName);
      formData.append('document_id', editingDocumentId);

      const response = await uploadAdditionalDocument(Number(export_id), formData);

      const updatedDocuments =
        response?.data?.export_additional_documents?.map((doc) => ({
          document_id: doc.document_id,
          name: doc.name,
          file: doc.file,
          document_url: doc.document_url,
        })) ?? [];

      if (!updatedDocuments) {
        message.error('Invalid response from server');
        return;
      }

      setDocuments(updatedDocuments);

      message.success('Document name updated successfully');
      if (onChange) {
        onChange({} as UploadFile, documentName, editingDocumentId);
      }
      setDocumentName('');
      setEditingDocumentId(null);
    } catch (err) {
      message.error('Failed to update document name.');
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: { document_id: string; document_url: string; name: string }) => (
        <Space>
          <Button onClick={() => handleView(record.document_id, record.document_url)} type="primary" size="small">
            View
          </Button>
          {editable && editableUser && (
            <Button onClick={() => handleEdit(record)} size="small">
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {editable && editableUser && (
        <>
          <Input
            placeholder="Enter document name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            disabled={uploading}
          />

          {editingDocumentId && (
            <Text type="warning">
              Editing document ID: <b>{editingDocumentId}</b>
            </Text>
          )}

          <Dragger {...props} disabled={uploading}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">Only one file can be uploaded per document.</p>
            {documentName && (
              <Text type="secondary">
                Document Name: <b>{documentName}</b>
              </Text>
            )}
          </Dragger>

          {editingDocumentId && (
            <Space style={{ marginTop: '8px' }}>
              <Button onClick={handleNameOnlyUpdate} loading={uploading} type="dashed">
                Save Name Only
              </Button>
              <Button
                danger
                type="link"
                onClick={() => {
                  setDocumentName('');
                  setEditingDocumentId(null);
                  setFileList([]);
                }}
              >
                Cancel
              </Button>
            </Space>
          )}
        </>
      )}

      <Table
        columns={columns}
        dataSource={documents}
        rowKey="document_id"
        pagination={false}
        style={{ marginTop: editable ? '16px' : 0 }}
      />
    </Space>
  );
};

export default AdditionalDocument;
